import { SelectChangeEvent, Typography } from '@mui/material';
import { useContext, useEffect } from 'react';

import { LocaleContext } from '@/Contexts/LocaleContext';
import { ProgramNames } from '@/models/Program';

import SelectProgram from '../../SelectProgram';
import { SelectedPartnerContext } from '@/Contexts/SelectedPartnerContext';

interface IntakeProgramSelectProps {
  onChange: (programId: ProgramNames) => void;
  /** Keep the user's current language until a program is chosen, instead of defaulting to English. */
  keepCurrentLocale?: boolean;
}

function IntakeProgramSelect({ onChange, keepCurrentLocale = false }: IntakeProgramSelectProps) {
  const { setOverrideLocale, clearOverrideLocale, translatedText } = useContext(LocaleContext);

  const { partnersInPartnerGroup } = useContext(SelectedPartnerContext);

  const determinedAvailableProgramOptionIds = (): ProgramNames[] | null => {
    const partnerProgramIds =
      partnersInPartnerGroup != null
        ? partnersInPartnerGroup
            .filter((partner) => partner.programId != null)
            .map((partner) => partner.programId as ProgramNames)
        : null;

    if (partnerProgramIds == null) return null;

    const availableOptions: ProgramNames[] = [];

    if (
      partnerProgramIds.includes(ProgramNames.TreeCitiesWorld) ||
      partnerProgramIds.includes(ProgramNames.TreeCityUSA) ||
      partnerProgramIds.includes(ProgramNames.TreeLineUSA)
    ) {
      availableOptions.push(
        ProgramNames.TreeCitiesWorld,
        ProgramNames.TreeCityUSA,
        ProgramNames.TreeLineUSA
      );
    }

    if (
      partnerProgramIds.includes(ProgramNames.TreeCampusHigherEducation) ||
      partnerProgramIds.includes(ProgramNames.TreeCampusHealthcare)
    ) {
      availableOptions.push(
        ProgramNames.TreeCampusHigherEducation,
        ProgramNames.TreeCampusHealthcare
      );
    }

    if (partnerProgramIds.includes(ProgramNames.TreeCampusK12)) {
      availableOptions.push(ProgramNames.TreeCampusK12);
    }

    return availableOptions?.length > 0 ? availableOptions : null;
  };

  const availableProgramSelectOptionIds: ProgramNames[] | null =
    determinedAvailableProgramOptionIds();

  const checkLanguageOverride = (selectedProgramId: ProgramNames) => {
    // Override locale to English if program is not TreeCitiesWorld
    if (selectedProgramId !== ProgramNames.TreeCitiesWorld && selectedProgramId != null) {
      setOverrideLocale('enUS');
    } else if (selectedProgramId === ProgramNames.TreeCitiesWorld) {
      clearOverrideLocale();
    }
  };

  useEffect(() => {
    if (keepCurrentLocale) return;
    setOverrideLocale('enUS');

    // only run on initial render to set default locale to English for program selection
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleProgramSelectChange = (event: SelectChangeEvent) => {
    const selectedProgramId = parseInt(event.target.value);
    checkLanguageOverride(selectedProgramId);
    onChange(selectedProgramId);
  };

  return (
    <>
      <Typography variant='h3'>{translatedText.signup.headerSelectProgram}</Typography>
      <SelectProgram
        onChange={handleProgramSelectChange}
        availableProgramOptionIds={availableProgramSelectOptionIds}
      />
    </>
  );
}

export default IntakeProgramSelect;
